// import { NumuwJoinedLogo } from "assets/icons";
import { Box, Button, Container, Flex, Text } from "components/Core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import logo from "../assets/icons/numuw_v2_logo_icon.svg";
import { recordNotificationClick } from "utils/notificationClick";
const routeQueryKeys = ["route"];
const pathPayloadQueryKeys = [
  "id",
  "payload",
  "payloads",
  "param",
  "params",
  "path",
  "paths",
  "segment",
  "segments",
  "pathPayload",
  "pathPayloads",
];
const structuredQueryKeys = ["query", "queryPayload", "queryParams"];
const reservedQueryKeys = new Set([
  ...routeQueryKeys,
  ...pathPayloadQueryKeys,
  ...structuredQueryKeys,
]);

const parseStructuredValue = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const toStringValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }

  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const toSegments = (value: string) =>
  value
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

const normalizePathValues = (values: string[]) => {
  const segments: string[] = [];

  values.forEach((value) => {
    const parsedValue = parseStructuredValue(value);

    if (Array.isArray(parsedValue)) {
      parsedValue.forEach((item) => {
        const stringValue = toStringValue(item);

        if (stringValue) {
          segments.push(...toSegments(stringValue));
        }
      });

      return;
    }

    const stringValue = toStringValue(parsedValue);

    if (stringValue) {
      segments.push(...toSegments(stringValue));
    }
  });

  return segments;
};

const appendQueryEntries = (
  queryParams: URLSearchParams,
  key: string,
  value: unknown,
) => {
  if (value === null || value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendQueryEntries(queryParams, key, item));
    return;
  }

  if (typeof value === "object") {
    queryParams.append(key, JSON.stringify(value));
    return;
  }

  queryParams.append(key, String(value));
};

const appendStructuredQueryPayload = (
  queryParams: URLSearchParams,
  values: string[],
) => {
  values.forEach((value) => {
    const parsedValue = parseStructuredValue(value);

    if (
      typeof parsedValue === "string" &&
      (parsedValue.includes("=") || parsedValue.includes("&"))
    ) {
      new URLSearchParams(parsedValue).forEach((queryValue, key) => {
        queryParams.append(key, queryValue);
      });
      return;
    }

    if (
      parsedValue &&
      typeof parsedValue === "object" &&
      !Array.isArray(parsedValue)
    ) {
      Object.entries(parsedValue).forEach(([key, queryValue]) => {
        appendQueryEntries(queryParams, key, queryValue);
      });
    }
  });
};

const buildDeepLink = (
  wildcardPath: string | undefined,
  searchParams: URLSearchParams,
) => {
  const pathSegments = wildcardPath ? toSegments(wildcardPath) : [];

  if (!pathSegments.length) {
    routeQueryKeys.forEach((key) => {
      pathSegments.push(...normalizePathValues(searchParams.getAll(key)));
    });
  }

  pathPayloadQueryKeys.forEach((key) => {
    pathSegments.push(...normalizePathValues(searchParams.getAll(key)));
  });

  const queryParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (!reservedQueryKeys.has(key)) {
      queryParams.append(key, value);
    }
  });

  structuredQueryKeys.forEach((key) => {
    appendStructuredQueryPayload(queryParams, searchParams.getAll(key));
  });

  const encodedPath = pathSegments
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  const queryString = queryParams.toString();

  if (!encodedPath) {
    return queryString ? `numuw://?${queryString}` : "numuw://";
  }

  return queryString
    ? `numuw://${encodedPath}?${queryString}`
    : `numuw://${encodedPath}`;
};

const OPEN_ATTEMPT_DELAY = 150;
const FALLBACK_DELAY = 1800;
const IOS_STORE_URL =
  "https://apps.apple.com/app/numuw-%D9%86%D9%85%D9%88/id6758160606";
const ANDROID_STORE_URL = "#";

const getStoreUrlByOs = () => {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  // iPadOS 13+ can report itself as Mac, so include touch-capable Mac devices.
  const isIOS =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1);
  const isMac = /Mac/i.test(platform) || /Macintosh|Mac OS X/i.test(userAgent);

  if (isIOS || isMac) {
    return IOS_STORE_URL;
  }

  return ANDROID_STORE_URL;
};

type LaunchState = "opening" | "fallback" | "opened";

const NumuwLinking = () => {
  const [searchParams] = useSearchParams();
  const { "*": wildcardPath } = useParams();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [launchState, setLaunchState] = useState<LaunchState>("opening");
  const fallbackTimeoutRef = useRef<number | undefined>(undefined);

  const deepLink = useMemo(
    () => buildDeepLink(wildcardPath, searchParams),
    [wildcardPath, location.search],
  );

  const notificationUuid = searchParams.get("notification_uuid");
  const recordedUuidRef = useRef<string | null>(null);

  // Record the click once per link, before the redirect takes the page away. The ref keeps
  // StrictMode's double-invoked effect (and any re-render) from counting the same click twice.
  useEffect(() => {
    if (!notificationUuid || recordedUuidRef.current === notificationUuid) {
      return;
    }

    recordedUuidRef.current = notificationUuid;
    recordNotificationClick(notificationUuid);
  }, [notificationUuid]);

  useEffect(() => {
    let openTimeoutId: number | undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setLaunchState("opened");
      }
    };

    setLaunchState("opening");
    document.addEventListener("visibilitychange", handleVisibilityChange);

    openTimeoutId = window.setTimeout(() => {
      window.location.assign(deepLink);
    }, OPEN_ATTEMPT_DELAY);

    return () => {
      if (fallbackTimeoutRef.current) {
        window.clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = undefined;
      }

      if (openTimeoutId) {
        window.clearTimeout(openTimeoutId);
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [deepLink]);

  const handleOpenApp = () => {
    if (fallbackTimeoutRef.current) {
      window.clearTimeout(fallbackTimeoutRef.current);
    }

    setLaunchState("opening");
    window.location.assign(deepLink);

    fallbackTimeoutRef.current = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        setLaunchState("fallback");
        window.location.assign(getStoreUrlByOs());
      }
    }, FALLBACK_DELAY);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deepLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Container
      minHeight="100vh"
      px={["20px", "24px", "40px"]}
      py={["32px", "48px"]}
    >
      <Flex
        flexDirection="column"
        width="100%"
        maxWidth="560px"
        gap="24px"
        padding={["24px", "32px", "40px"]}
        borderRadius="28px"
        backgroundColor="white"
      >
        <img
          src={logo}
          alt="Numuw Joined Logo"
          style={{ maxWidth: "180px", height: "auto", alignSelf: "center" }}
        />

        <Flex flexDirection="column" gap="10px" alignItems="center">
          {/* <Box
            width="72px"
            height="72px"
            borderRadius="50%"
            background="#f3e9ff"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "30px",
            }}
          >
            {launchState === "opened" ? "✓" : "↗"}
          </Box> */}
          <Text variant="heading3" margin={0} textAlign="center">
            Open Numuw in the app
          </Text>
          <Text variant="body6" color="#666" margin={0} textAlign="center">
            If the app doesn't open automatically, click the button below to
            open it manually.
          </Text>
        </Flex>

        {/* <Box
          padding={["16px", "20px", "24px"]}
          border="1px solid #E9DDF4"
          borderRadius="20px"
          background="#FAF7FD"
        >
          <Text variant="body6" color="#8450A0" margin="0 0 8px">
            Generated link
          </Text>
          <Text
            as="code"
            margin={0}
            color="#1E2430"
            fontSize={["14px", "15px", "16px"]}
            style={{
              display: "block",
              wordBreak: "break-word",
              fontFamily: "monospace",
            }}
          >
            {deepLink}
          </Text> 
        </Box> */}

        <Flex flexWrap="wrap" gap="12px" justifyContent="center">
          <button
            className="bg-[#4242CE] hover:bg-indigo-700 text-white text-sm font-medium px-4 py-3 rounded-xl transition-colors duration-200 whitespace-nowrap max-w-fit"
            // color={"primary"}

            type="button"
            onClick={handleOpenApp}
          >
            Open in app
          </button>
          {/* <Button type="button" variant="secondary" onClick={handleCopy}>
            {copied ? "Copied" : "Copy link"}
          </Button> */}
        </Flex>
      </Flex>
    </Container>
  );
};

export default NumuwLinking;
export { NumuwLinking };
